/**
 * MoonPay country routing — the SINGLE source of truth for headless vs hosted.
 *
 * headless (Customer-API): the 16 LatAm markets MoonPay confirmed for direct
 *   KYC via our own screens. Uses lib/moonpay frames + MoonpayKycStep.
 * hosted (signed widget): everything else. MoonPay owns the whole UI + KYC.
 *
 * There is no runtime fallback between the two — a country is one or the other,
 * decided once from the selector. Keep MOONPAY_HEADLESS_COUNTRIES in sync with
 * the backend `HEADLESS_COUNTRIES` (moonpay.service.js); the backend also returns
 * its set in /moonpay/config so the client can prefer that when present.
 */

export interface MoonpayCountry {
  /** ISO 3166-1 alpha-2 — used for routing + the widget. */
  a2: string;
  /** ISO 3166-1 alpha-3 — required by the Customer API residentialAddress.country. */
  a3: string;
  name: string;
  /** Local fiat (ISO 4217) used to prefill quotes/widget. */
  fiat: string;
}

// The 16 LatAm markets → HEADLESS (our own KYC screens).
export const MOONPAY_HEADLESS_COUNTRIES: MoonpayCountry[] = [
  { a2: 'MX', a3: 'MEX', name: 'Mexico', fiat: 'MXN' },
  { a2: 'AR', a3: 'ARG', name: 'Argentina', fiat: 'ARS' },
  { a2: 'BR', a3: 'BRA', name: 'Brazil', fiat: 'BRL' },
  { a2: 'CL', a3: 'CHL', name: 'Chile', fiat: 'CLP' },
  { a2: 'CO', a3: 'COL', name: 'Colombia', fiat: 'COP' },
  { a2: 'PE', a3: 'PER', name: 'Peru', fiat: 'PEN' },
  { a2: 'UY', a3: 'URY', name: 'Uruguay', fiat: 'UYU' },
  { a2: 'PY', a3: 'PRY', name: 'Paraguay', fiat: 'PYG' },
  { a2: 'CR', a3: 'CRI', name: 'Costa Rica', fiat: 'CRC' },
  { a2: 'PA', a3: 'PAN', name: 'Panama', fiat: 'USD' },
  { a2: 'GT', a3: 'GTM', name: 'Guatemala', fiat: 'GTQ' },
  { a2: 'DO', a3: 'DOM', name: 'Dominican Republic', fiat: 'DOP' },
  { a2: 'SV', a3: 'SLV', name: 'El Salvador', fiat: 'USD' },
  { a2: 'HN', a3: 'HND', name: 'Honduras', fiat: 'HNL' },
  { a2: 'EC', a3: 'ECU', name: 'Ecuador', fiat: 'USD' },
  { a2: 'BO', a3: 'BOL', name: 'Bolivia', fiat: 'BOB' },
];

// Everything else → HOSTED (signed widget). Curated common markets for the
// selector; the widget itself lets the user adjust currency/method. Extend as
// needed — any country not listed here is still treated as hosted.
export const MOONPAY_HOSTED_COUNTRIES: MoonpayCountry[] = [
  { a2: 'US', a3: 'USA', name: 'United States', fiat: 'USD' },
  { a2: 'CA', a3: 'CAN', name: 'Canada', fiat: 'CAD' },
  { a2: 'GB', a3: 'GBR', name: 'United Kingdom', fiat: 'GBP' },
  { a2: 'AU', a3: 'AUS', name: 'Australia', fiat: 'AUD' },
  { a2: 'NZ', a3: 'NZL', name: 'New Zealand', fiat: 'NZD' },
  { a2: 'ES', a3: 'ESP', name: 'Spain', fiat: 'EUR' },
  { a2: 'DE', a3: 'DEU', name: 'Germany', fiat: 'EUR' },
  { a2: 'FR', a3: 'FRA', name: 'France', fiat: 'EUR' },
  { a2: 'IT', a3: 'ITA', name: 'Italy', fiat: 'EUR' },
  { a2: 'PT', a3: 'PRT', name: 'Portugal', fiat: 'EUR' },
  { a2: 'NL', a3: 'NLD', name: 'Netherlands', fiat: 'EUR' },
  { a2: 'IE', a3: 'IRL', name: 'Ireland', fiat: 'EUR' },
  { a2: 'ZA', a3: 'ZAF', name: 'South Africa', fiat: 'ZAR' },
  { a2: 'AE', a3: 'ARE', name: 'United Arab Emirates', fiat: 'AED' },
];

/** Full selector list — headless markets first, then curated hosted markets. */
export const MOONPAY_COUNTRIES: MoonpayCountry[] = [
  ...MOONPAY_HEADLESS_COUNTRIES,
  ...MOONPAY_HOSTED_COUNTRIES,
];

const HEADLESS_A2 = new Set(MOONPAY_HEADLESS_COUNTRIES.map((c) => c.a2));

/**
 * Is `a2` a headless (Customer-API) country? Pass the backend's set (from
 * /moonpay/config → headlessCountries) as `override` to defer to it when present.
 */
export function isHeadlessCountry(a2?: string, override?: string[] | Set<string>): boolean {
  const cc = String(a2 || '').toUpperCase();
  if (override && (Array.isArray(override) ? override.length : override.size)) {
    const set = override instanceof Set ? override : new Set(override.map((s) => s.toUpperCase()));
    return set.has(cc);
  }
  return HEADLESS_A2.has(cc);
}

export function moonpayCountry(a2?: string): MoonpayCountry | undefined {
  const cc = String(a2 || '').toUpperCase();
  return MOONPAY_COUNTRIES.find((c) => c.a2 === cc);
}

/** Local fiat (ISO 4217) for a country; defaults to USD when unknown. */
export function fiatForCountry(a2?: string): string {
  return (moonpayCountry(a2)?.fiat || 'USD').toUpperCase();
}

/** ISO alpha-3 for a country (empty string when unknown). */
export function alpha3ForCountry(a2?: string): string {
  return moonpayCountry(a2)?.a3 || '';
}
